import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClient, useUsers } from '@/api/base';
import { api } from '@/api/client';
import { useDeal, useM3Mutation } from '@/api/m3';
import type { ChecklistKey, DealCard as Deal, DealStage } from '@/api/m3-types';
import { useMe } from '@/api/queries';
import { Button } from '@/components/kit/Button';
import { Breadcrumbs, Kv } from '@/components/kit/Layout';
import { CHECKLIST_LEGEND, Legend, StatusDot } from '@/components/kit/Marks';
import { MetricRow } from '@/components/kit/MetricRow';
import { Alert, ErrorState, Note, Skeleton } from '@/components/kit/Note';
import { OwnerPill } from '@/components/kit/OwnerPill';
import { Overview, Panel, Signals } from '@/components/kit/Panel';
import { PersonHeader } from '@/components/kit/Person';
import { Pill } from '@/components/kit/Pill';
import { Stepper } from '@/components/kit/Stepper';
import { useToast } from '@/components/kit/Toast';
import { money, people } from '@/lib/format';
import { ClientDataRows } from '@/features/base/clients/ClientData';
import { EditClientModal } from '@/features/base/clients/ClientDialogs';
import { errorText } from '@/features/base/shared/useUndoable';
import { daysText, fullDateTime, READINESS, REG_STATUS, STAGES, stageTitle } from '../labels';
import { M3Feed, TasksStub } from '../M3Feed';
import { useM3Action } from '../useM3Submit';
import { ForecastModal, LoseModal } from './DealDialogs';

type Dialog = 'lose' | 'forecast';

export const CHECKLIST_COLUMNS: { key: ChecklistKey; label: string }[] = [
  { key: 'comp', label: 'Состав' },
  { key: 'pay', label: 'Оплата' },
  { key: 'music', label: 'Музыка' },
  { key: 'video', label: 'Видео' },
  { key: 'costume', label: 'Костюмы' },
  { key: 'admission', label: 'Допуск' },
];

/** Проигрыш перетаскиванием на доске — то же окно, что в карточке */
export function LoseByDropModal({ dealId, onClose }: { dealId: string; onClose: () => void }) {
  const card = useDeal(dealId);
  if (!card.data) return null;
  return <LoseModal deal={card.data} onClose={onClose} />;
}

/**
 * Карточка сделки (viewDeal в макете, ТЗ 5.2): степпер стадий, цифры,
 * номера с чек-листом, данные и источник, задачи и лента. Ответственный —
 * пилюлей: меняется ответственный клиента в базе (БЛ 5.11).
 */
export function DealPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const card = useDeal(dealId);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const close = () => setDialog(null);
  const crumbs = (last: string) => (
    <Breadcrumbs
      items={[
        { label: 'CRM', onClick: () => navigate('/crm/deals') },
        { label: 'Сделки', onClick: () => navigate('/crm/deals') },
        { label: last },
      ]}
    />
  );

  if (card.isError) {
    return (
      <div className="main">
        {crumbs('Сделка')}
        <ErrorState message={card.error.message} onRetry={() => void card.refetch()} />
      </div>
    );
  }
  if (card.isLoading || !card.data) {
    return (
      <div className="main">
        {crumbs('…')}
        <Skeleton width={320} height={28} />
        <div className="sigs" />
        <Skeleton width="60%" />
      </div>
    );
  }

  const d = card.data;
  const open = d.figures.active + d.figures.reserve;
  const idleOver = d.stage !== 'lost' && d.stage !== 'won' && d.idleDays >= d.idleLimit;

  return (
    <div className="main has-abar">
      {crumbs(`${d.client.shortName} · ${d.event.city}`)}
      <DealHeader deal={d} onDialog={setDialog} />
      <DealSignals deal={d} open={open} idleOver={idleOver} />
      <DealStepper deal={d} open={open} />
      <MetricRow
        variant="sum"
        className="m3"
        items={[
          { label: 'План', value: money(d.figures.plan), sub: `прогноз ${d.forecast ? money(d.forecast) : '—'}` },
          { label: 'Факт', value: money(d.figures.fact), sub: 'оплачено' },
          { label: 'Долг', value: money(d.figures.debt) },
          {
            label: 'Номеров',
            value: d.figures.active,
            sub: d.figures.reserve ? `и в резерве ${d.figures.reserve}` : undefined,
          },
          { label: 'Без движения', value: daysText(d.idleDays), over: idleOver },
        ]}
      />
      <div className="m3gap">
        <RegistrationsPanel deal={d} />
      </div>
      <Overview
        left={
          <>
            <ClientPanel clientId={d.client.id} />
            <DataPanel deal={d} onForecast={() => setDialog('forecast')} />
            <SourcePanel deal={d} />
          </>
        }
        right={
          <>
            <TasksStub of="сделке" />
            <M3Feed scope="deals" id={d.id} />
          </>
        }
      />
      {dialog === 'lose' ? <LoseModal deal={d} onClose={close} /> : null}
      {dialog === 'forecast' ? <ForecastModal deal={d} onClose={close} /> : null}
    </div>
  );
}

function DealHeader({ deal: d, onDialog }: { deal: Deal; onDialog: (d: Dialog) => void }) {
  const navigate = useNavigate();
  const users = useUsers();
  const me = useMe();
  const toast = useToast();
  const setOwner = useM3Mutation((userId: string) => api.put(`/deals/${d.id}/owner`, { userId }));
  return (
    <PersonHeader
      initials={d.client.initials}
      title={d.client.fullName}
      actions={
        <>
          <OwnerPill
            owner={d.owner}
            users={users.data ?? []}
            meId={me.data?.id}
            emptyLabel="Не назначен"
            onChange={(id) =>
              id &&
              setOwner.mutate(id, {
                onSuccess: () =>
                  toast.show(
                    `Ответственный: ${users.data?.find((u) => u.id === id)?.fullName ?? ''} — он же ответственный клиента в базе`,
                  ),
                onError: (e) => toast.showError(errorText(e)),
              })
            }
          />
          {d.lost ? null : (
            <Button tone="primary" onClick={() => navigate(`/registrations/new?dealId=${d.id}`)}>
              ＋ Регистрация
            </Button>
          )}
          <Button onClick={() => navigate(`/clients/${d.client.id}`)}>Карточка клиента</Button>
          {d.lost ? null : <Button onClick={() => onDialog('lose')}>Отметить проигрыш…</Button>}
        </>
      }
    />
  );
}

function DealSignals({ deal: d, open, idleOver }: { deal: Deal; open: number; idleOver: boolean }) {
  const action = useM3Action();
  return (
    <Signals>
      {d.lost ? (
        <Alert
          tone="mut"
          action={
            <Button small disabled={action.pending} onClick={() => action.run(() => api.post(`/deals/${d.id}/revive`), 'Сделка снова в работе: «Новая»')}>
              Вернуть в работу
            </Button>
          }
        >
          <b>Проиграна {d.lost.at.split('-').reverse().join('.')}.</b>
          <span>
            {d.lost.reason}
            {d.lost.comment ? ` — ${d.lost.comment}` : ''}
            {d.lost.by ? ` · ${d.lost.by}` : ''}
          </span>
        </Alert>
      ) : null}
      {!d.lost && !open && d.registrations.length ? (
        <Alert tone="mut">
          <b>Открытых регистраций не осталось.</b>
          <span>
            Номера сняты или перенесены — сделка вернулась на стадию «{stageTitle(d.manualStage)}». Работайте дальше или
            отметьте проигрыш.
          </span>
        </Alert>
      ) : null}
      {idleOver ? (
        <Alert>
          <b>Без движения {daysText(d.idleDays)}.</b>
          <span>
            Порог в настройках — {daysText(d.idleLimit)}. Позже здесь система поставит задачу «оживить сделку».
          </span>
        </Alert>
      ) : null}
    </Signals>
  );
}

/**
 * Степпер: мятным — ровно одна текущая стадия. Первые две двигает
 * менеджер, пока нет открытых номеров; три последние ставит система.
 */
function DealStepper({ deal: d, open }: { deal: Deal; open: number }) {
  const action = useM3Action();
  const idx = STAGES.findIndex((s) => s.key === d.stage);
  const move = (stage: DealStage) =>
    action.run(() => api.post(`/deals/${d.id}/stage`, { stage }), `Стадия изменена: «${stageTitle(stage)}»`);
  return (
    <Stepper
      steps={STAGES.map((s, n) => {
        const clickable = !s.auto && !open && !d.lost && s.key !== d.stage;
        return {
          key: s.key,
          title: s.title,
          caption: `${s.auto ? 'ставит система' : 'менеджер'}${n === idx ? ' · сейчас здесь' : ''}`,
          state: d.lost || idx < 0 ? 'next' : n < idx ? 'done' : n === idx ? 'on' : 'next',
          hint: s.auto ? s.why : open ? 'В сделке есть регистрации — стадия считается сама' : 'Перевести сделку',
          onClick: clickable ? () => move(s.key) : undefined,
        };
      })}
    />
  );
}

function RegistrationsPanel({ deal: d }: { deal: Deal }) {
  const navigate = useNavigate();
  const [y, m1, d1] = d.event.dateFrom.split('-');
  const [, m2, d2] = d.event.dateTo.split('-');
  const dates = d.event.dateFrom === d.event.dateTo ? `${d1}.${m1}.${y}` : m1 === m2 ? `${d1}–${d2}.${m2}.${y}` : `${d1}.${m1}–${d2}.${m2}.${y}`;
  return (
    <Panel flush title={`Регистрации на ${d.event.city} · ${dates}`} count={d.registrations.length}>
      {d.registrations.length ? (
        <>
          <table className="tbl">
            <thead>
              <tr>
                <th>Номер</th>
                <th className="r">План</th>
                <th className="r">Факт</th>
                {CHECKLIST_COLUMNS.map((c) => (
                  <th key={c.key} className="rc">
                    {c.label}
                  </th>
                ))}
                <th>Состояние</th>
              </tr>
            </thead>
            <tbody>
              {d.registrations.map((x) => {
                const muted = x.status === 'declined' || x.status === 'moved';
                const state = x.status === 'active' ? READINESS[x.readiness] : REG_STATUS[x.status];
                return (
                  <tr key={x.id} className="clk" onClick={() => navigate(`/registrations/${x.id}`)}>
                    <td data-col="primary">
                      <div className={muted ? 'nm muted' : 'nm'}>{x.who}</div>
                      <div className="sub">
                        {x.nomination.name} · {people(x.liveRows)}
                      </div>
                    </td>
                    <td className="r num" data-col="secondary">
                      {x.status === 'active' ? money(x.billed) : <span className="na">—</span>}
                    </td>
                    <td className="r num">
                      {x.status === 'active' ? (
                        money(x.paid)
                      ) : x.toRefund ? (
                        <>
                          <span className="na">—</span>
                          <div className="sub">к возврату {money(x.toRefund)}</div>
                        </>
                      ) : (
                        <span className="na">—</span>
                      )}
                    </td>
                    {CHECKLIST_COLUMNS.map((c) => (
                      <td key={c.key} className="rc">
                        <StatusDot state={muted ? 'na' : x.checklist[c.key]} />
                      </td>
                    ))}
                    <td>
                      <Pill tone={state.tone}>{state.label}</Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Legend items={CHECKLIST_LEGEND} />
        </>
      ) : (
        <Note>
          Регистраций нет — в сумме сделки только прогноз. Номинации и цены берутся из мероприятия {d.event.label}.
        </Note>
      )}
    </Panel>
  );
}

/**
 * Клиент сделки (Road Map п. 12): все данные блока «Данные» карточки
 * клиента, без пустых, — список полей общий с ней. «Изменить» — то же
 * окно, что в карточке клиента: те же проверки и запись в его ленту.
 */
function ClientPanel({ clientId }: { clientId: string }) {
  const client = useClient(clientId);
  const [editing, setEditing] = useState(false);
  const c = client.data;
  return (
    <Panel
      title="Клиент"
      actions={
        c ? (
          <Button small onClick={() => setEditing(true)}>
            Изменить
          </Button>
        ) : undefined
      }
    >
      {client.isError ? (
        <ErrorState message={client.error.message} onRetry={() => void client.refetch()} />
      ) : !c ? (
        <>
          <Skeleton />
          <Skeleton width="70%" />
          <Skeleton width="50%" />
        </>
      ) : (
        <ClientDataRows client={c} compact />
      )}
      {editing && c ? <EditClientModal client={c} onClose={() => setEditing(false)} /> : null}
    </Panel>
  );
}

function DataPanel({ deal: d, onForecast }: { deal: Deal; onForecast: () => void }) {
  const navigate = useNavigate();
  return (
    <Panel
      title="Данные сделки"
      actions={
        <Button small onClick={onForecast}>
          Изменить прогноз
        </Button>
      }
    >
      <Kv k="Клиент">
        <span className="lnk" onClick={() => navigate(`/clients/${d.client.id}`)}>
          {d.client.shortName}
        </span>
      </Kv>
      <Kv k="Мероприятие">{d.event.full}</Kv>
      <Kv k="Прогноз">{d.forecast ? money(d.forecast) : <span className="dim">не задан</span>}</Kv>
      <Kv k="Ответственный">{d.owner ? d.owner.fullName : <span className="wtx">не назначен у клиента</span>}</Kv>
      <Kv k="Создана">
        <span className="num">{fullDateTime(d.createdAt)}</span>
      </Kv>
    </Panel>
  );
}

function SourcePanel({ deal: d }: { deal: Deal }) {
  const navigate = useNavigate();
  const lead = d.source.lead;
  const utm = [lead.utm.source, lead.utm.medium, lead.utm.campaign].filter(Boolean);
  return (
    <Panel title="Источник">
      <Kv k="Канал">{lead.source}</Kv>
      <Kv k="UTM-метки">{utm.length ? <span className="brk">{utm.join(' / ')}</span> : <span className="dim">нет</span>}</Kv>
      <Kv k="Первая заявка">
        <span className="lnk" onClick={() => navigate(`/crm/leads/${lead.id}`)}>
          #{lead.id}
        </span>{' '}
        <span className="muted">· {fullDateTime(lead.createdAt)}</span>
      </Kv>
      {d.source.others.length ? (
        <Kv k="Ещё заявки">
          {d.source.others.map((o) => (
            <div key={o.id}>
              <span className="lnk" onClick={() => navigate(`/crm/leads/${o.id}`)}>
                #{o.id}
              </span>{' '}
              <span className="muted">· {o.source}</span>
            </div>
          ))}
        </Kv>
      ) : null}
      <Note>Метки читаются из заявки и не копируются в сделку — у каждой заявки своя атрибуция.</Note>
    </Panel>
  );
}

